/**
 * Name a session once, after its first turn (RFC 0311 3b).
 *
 * A session is born with a stub title: the first line someone typed, cut at 60
 * characters, or "Session 3". After the agent's first turn ends, one call to a
 * small model turns that into two or three words that name the work, and
 * `nameSessionOnce` writes it only if the registry still says the title is a
 * stub. A person's name is never replaced, and a second turn never renames.
 *
 * WHY THE HOST. The host is the process that sees a run go from running to
 * idle, and it holds everything the write needs: the repo, the project id, the
 * session's live handle and an inference client. Same reasoning as the status
 * mirror beside it.
 *
 * ONE CALL, EVER, PER HOSTED SESSION. A failed or unusable answer leaves the
 * stub; there is no retry loop, because a session that cannot be named is a
 * cosmetic miss and a naming call that repeats on every change is a bill.
 */

import type { DocHandle, Repo } from '@automerge/automerge-repo/slim'
import type { TranscriptMessage } from '../log/projection-transcript'
import type { ModelRef } from '../log/types'
import { markNameTried, nameSessionOnce } from '../model/session/mutations'
import { projectSessionTranscript } from '../model/session/reads'
import { awaitingName } from '../model/session/title'
import type { SessionMetaEntry, SessionRunStatus } from '../model/session/types'
import type { InferenceClient } from './seams'

/** What the model is shown: the start of the conversation, nothing more. */
export interface NamingInput {
  firstMessage: string
  firstReply: string
}

/** Ask a model for a name. Injected so tests and hosts choose the client. */
export type NameFn = (input: NamingInput) => Promise<string>

export interface NamerDeps {
  repo: Repo
  projectId: string
  sessionId: string
  handle: DocHandle<unknown>
  /** The project document as it is now; the registry the guard reads. */
  projectDoc: () => unknown
  name: NameFn
  onWarn?: (message: string) => void
}

export interface SessionNamer {
  /** The session's status after every log change, from the status mirror:
   *  the namer projects nothing itself. */
  onStatus(status: SessionRunStatus): void
  stop(): void
}

/** How much of each side the model sees. Enough to name the work, and cheap. */
const EXCERPT = 600

/**
 * One to three plain words, or nothing.
 *
 * The model is asked for exactly this, and this makes sure: punctuation and
 * quotes go, whitespace collapses, anything past three words is cut. An answer
 * with no words left is refused, which keeps the stub. An ampersand stays,
 * because it is part of names like "I&S" and "R&D".
 */
export function cleanTitle(raw: string): string | null {
  const words = raw
    .replace(/[^\p{L}\p{N}\s&-]/gu, ' ')
    .split(/\s+/)
    .filter((w) => w && w !== '-' && w !== '&')
    .slice(0, 3)
  const title = words.join(' ').slice(0, 40).trim()
  return title ? title : null
}

/**
 * The note a harness writes when a turn is stopped (`_(stopped)_`). It is not
 * something the agent said: a first turn stopped before any words has no
 * reply yet, and naming from the note called a session "stopped".
 */
const STOPPED_NOTE = /_?\(stopped\)_?/g

function textOf(message: TranscriptMessage | undefined): string {
  if (!message) return ''
  return message.content
    .map((b) => (b.type === 'text' ? String(b.text) : ''))
    .join(' ')
    .replace(STOPPED_NOTE, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, EXCERPT)
}

/** The first thing asked and the first thing answered, once both exist. */
export function namingInput(sessionDoc: unknown): NamingInput | null {
  const transcript = projectSessionTranscript(sessionDoc)
  const firstMessage = textOf(transcript.find((m) => m.role === 'user'))
  const firstReply = textOf(
    transcript.find((m) => m.role === 'assistant' && textOf(m)),
  )
  return firstMessage && firstReply ? { firstMessage, firstReply } : null
}

export function nameSessionAfterFirstTurn(deps: NamerDeps): SessionNamer {
  let done = false
  let busy = false

  const entry = () =>
    (deps.projectDoc() as { sessionMeta?: Record<string, SessionMetaEntry> })
      ?.sessionMeta?.[deps.sessionId]

  const finish = () => {
    done = true
  }

  // Called from the status mirror's deferred read, never inside a change
  // handler, so reading the session here cannot re-enter the wasm mid-apply.
  function check(status: SessionRunStatus) {
    if (done || busy) return
    // Not a stub: a person or an agent already chose this name. Nothing to do,
    // now or on any later change.
    if (!awaitingName(entry())) return finish()
    // Tried before, by this host or another, before a restart or a re-host:
    // one model call per session, ever, not one per process.
    if (entry()?.nameTriedAt) return finish()
    if (status !== 'idle') return
    const input = namingInput(deps.handle.doc())
    if (!input) return
    busy = true
    // The attempt is recorded before the call, so a crash mid-call does not
    // earn a second one, and two hosts do not both ask.
    void markNameTried(deps.repo, deps.projectId, deps.sessionId)
      .then(async (marked) => {
        if (!marked) return
        const title = cleanTitle(await deps.name(input))
        if (title)
          await nameSessionOnce(
            deps.repo,
            deps.projectId,
            deps.sessionId,
            title,
          )
      })
      .catch((err) =>
        deps.onWarn?.(
          `session ${deps.sessionId}: not named: ${(err as Error)?.message ?? String(err)}`,
        ),
      )
      .finally(() => {
        busy = false
        finish()
      })
  }

  return { onStatus: check, stop: finish }
}

/**
 * The instruction the model gets.
 *
 * A LABEL, NOT A SUMMARY. The names people give sessions by hand are the
 * bucket the work belongs to: a feature ("session identity", "app icons") or
 * a partner or project ("I&S"). A summary of the first request ("Fix sidebar
 * icon size") is the name nobody would have typed. So the model is told to
 * reuse a name the conversation already uses, and to write it the way it was
 * written.
 */
export const NAMING_PROMPT =
  'Give this work a short label, the way you would name a folder for it. ' +
  'Label the feature, product, project or partner it belongs to, not the ' +
  'task. If the conversation names it (a feature name, an RFC title, a ' +
  'partner or company name, an acronym), use that name as written. One to ' +
  'three words, lowercase except for proper names and acronyms. No quotes, ' +
  'no full stop. Reply with the label only.'

/** Longest answer read before giving up on it: a name is a few words. */
const MAX_ANSWER = 200

/**
 * A `NameFn` over the host's own inference client.
 *
 * TEXT ONLY, AND SHORT. The client is meant to be a raw model stream, but some
 * clients drive a whole agent. The first tool call or tool result ends the
 * answer and it is refused, and reading stops past a few hundred characters,
 * so a client that is not a plain model can cost one short exchange and no more.
 */
export function nameWith(client: InferenceClient, model: ModelRef): NameFn {
  return async ({ firstMessage, firstReply }) => {
    const prompt =
      `${NAMING_PROMPT}\n\nThe person asked:\n${firstMessage}\n\n` +
      `The agent answered:\n${firstReply}`
    let answer = ''
    for await (const chunk of client.stream(
      [{ role: 'user', content: [{ type: 'text', text: prompt }] }],
      { model },
    )) {
      if (chunk.toolUse || chunk.toolResult)
        throw new Error('the naming client answered with a tool call')
      if (chunk.text) answer += chunk.text
      if (answer.length > MAX_ANSWER) break
    }
    return answer
  }
}
